<template>
  <div class="card-container">
    <div v-if="title || rightText || $slots.bottom" class="title-area">
      <div v-if="title || rightText" class="top-section">
        <span class="title">{{ title }}</span>
        <slot name="right">
          <router-link
            v-if="rightText"
            class="right"
            :to="rightLink"
            @click="handleRightTextClick"
          >
            {{ rightText }}
            <BaseSvg :src="ChevronRight" />
          </router-link>
        </slot>
      </div>
      <div v-if="$slots.bottom" class="bottom-section">
        <slot name="bottom" />
      </div>
    </div>

    <div class="cards-wrapper" :class="cardWrapperClasses">
      <div v-if="loading" class="card-loader">
        <BaseLoader class="loader" />
      </div>
      <div
        v-for="(slotNames, index) in cardList"
        :key="index"
        class="card"
        :class="customCardClass"
      >
        <slot :name="slotNames" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BaseSvg, BaseLoader } from '@/components/Base';
import { ChevronRight } from '@/assets/icons';
import { computed, useSlots } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

export interface ICard {
  title?: string;
  rightText?: string;
  loading?: boolean;
  customCardClass?: string;
  customCardWrapperClass?: string;
  isDirectionRow?: boolean;
  rightLink?: RouteLocationRaw;
}
const props = withDefaults(defineProps<ICard>(), {
  loading: false,
  title: '',
  rightText: '',
  customCardClass: '',
  customCardWrapperClass: '',
  isDirectionRow: false,
  rightLink: () => ({}) as RouteLocationRaw,
});
const emit = defineEmits<{
  rightTextClick: [event: Event];
}>();

const slots = useSlots();

const cardWrapperClasses = computed(() => {
  return {
    'direction-row': props.isDirectionRow,
    [props.customCardWrapperClass]: props.customCardWrapperClass,
  };
});

const cardList = computed(() => {
  const slotNames = Object.keys(slots).filter((slotName) =>
    slotName.startsWith('card-')
  );
  return slotNames.length ? slotNames : ['default'];
});

function handleRightTextClick(e: Event) {
  emit('rightTextClick', e);
}
</script>

<style scoped lang="scss">
.card-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: $text;
  font-weight: 500;
  font-size: $text-size-lg;
  gap: 24px;
  & .title-area {
    display: flex;
    flex-direction: column;
    width: 100%;
    color: $text-light-200;
    gap: 16px;
    .top-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: 38px;
    }
    .bottom-section {
      width: 100%;
    }
    .title {
      font-weight: 600;
      font-size: $text-size-xxl;
      line-height: 120%;
      letter-spacing: -0.02em;
      color: $text;
    }
    & .right {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 14px;
      &:hover {
        color: $primary-dark-100;
      }
    }
  }
  & .cards-wrapper {
    display: grid;
    grid-template-columns: repeat(1, minmax(300px, 1fr));
    gap: 2rem;
    width: 100%;
    position: relative;
    flex: 1;
    justify-content: flex-start;
    align-items: center;
    &.direction-row {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }
    & .card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      @include card-box;
    }
  }

  & .card-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    position: absolute;
    background-color: rgba($white, 0.4);
    border-radius: $border-radius-lg;
    :deep(.loader-wrapper) {
      background-color: transparent;
    }
    .loader {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
